import { Component, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Grid, Radio, TextField } from '@mui/material';
import _, { flowRight as compose } from 'lodash';
import { inject, observer } from 'mobx-react';
import { List } from 'react-window';

import avatarHolder from '../../assets/images/avatar-placeholder.jpg';
import JLoading from '../JLoading';
import styles from './styles';

const Row = ({ index, style, selectedDriverId, onSelectDriver, drivers }) => {
  const driver = drivers?.[index];
  if (!driver) return null;
  const driverName = _.capitalize(driver.first_name) + ' ' + _.capitalize(driver.last_name);
  const isSelected = driver.id === selectedDriverId;
  const disabled = !driver.background_status || driver.background_status.indexOf('APPROVED') < 0;

  return (
    <Grid key={index} style={style}>
      <Box
        sx={{
          ...styles.cardContainer,
          ...(isSelected && styles.cardSelected),
          ...(disabled && styles.cardDisabled),
        }}
      >
        <Grid container spacing={1} onClick={onSelectDriver(driver.id, disabled)}>
          <Grid size={2} style={{ alignSelf: 'center' }}>
            <Box>
              <img src={driver.photo || avatarHolder} alt={driverName} style={styles.cardAvatar} />
            </Box>
          </Grid>
          <Grid size={8}>
            <Grid container direction="column" justifyContent="space-between" style={{ height: '100%' }}>
              <Box p={0.5} sx={styles.cardLabel}>
                {driverName}
              </Box>
              <Box p={0.5} sx={{ ...styles.cardSubtitle, ...styles.grayText }}>
                {driver.assignment_count || 'No'} Assignments
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

class AssignDriver extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDriverId: props.driverIdSelected,
      selectedVehicleId: null,
      selectedVehicleIdAlias: null,
      searchKey: '',
      personalVehicles: [],
      companyVehicles: [],
      personalVehicleLoading: false,
    };
    this.ITEMSIZE = 78;
    this.HEIGHT = 390;
  }

  componentDidUpdate(prevProps) {
    const { driverIdSelected } = this.props;

    // React to driverIdSelected prop changes
    if (driverIdSelected !== prevProps.driverIdSelected) {
      this.setState({ selectedDriverId: driverIdSelected }, this.emitSelectionChange);
      this.getPersonalVehicles(driverIdSelected);
      this.onProcessVehicleSelected();
    }
  }

  componentDidMount() {
    const { driverStore, userStore } = this.props.store;
    const { driverIdSelected } = this.props;

    // Load driver's list
    driverStore.listActiveDrivers();
    // Load vehicle's driver
    this.getPersonalVehicles(driverIdSelected);
    // Load vehicle's company
    this.getCompanyVehicles();
    this.onProcessVehicleSelected();
    // Emit the initial selection (e.g. preselected driver on reassign)
    this.emitSelectionChange();
  }

  // Bubble the current selection (driver + vehicle) up to the parent, which owns
  // the footer action buttons and the assign API call.
  emitSelectionChange = () => {
    const { onSelectionChange } = this.props;
    if (!onSelectionChange) return;
    const { selectedDriverId, selectedVehicleId } = this.state;
    const { drivers } = this.props.store.driverStore;
    const driver = (drivers || []).find((dr) => dr.id === selectedDriverId) || null;
    onSelectionChange({ driverId: selectedDriverId, driver, vehicleId: selectedVehicleId });
  };

  onProcessVehicleSelected = () => {
    const selectedVehicles = _.get(this, 'props.store.assignmentStore.selectedAssignment.assignment_driver_vehicles');
    const vehicleSelected = _.filter(selectedVehicles, (v) => v.driver_vehicle_id)[0];

    if (vehicleSelected) {
      this.setState({ selectedVehicleId: vehicleSelected.driver_vehicle_id }, this.emitSelectionChange);
    }
  };

  onSelectDriver = (id, disabled) => () => {
    if (disabled) return;
    this.setState({ selectedDriverId: id }, this.emitSelectionChange);
    // Load vehicle's driver
    this.getPersonalVehicles(id);
  };

  onSelectPersonalVehicle = (id) => {
    this.setState({
      selectedVehicleId: id,
      selectedVehicleIdAlias: `PER_${id}`,
    }, this.emitSelectionChange);
  };

  onSelectCompanyVehicle = (id) => {
    this.setState({
      selectedVehicleId: id,
      selectedVehicleIdAlias: `COM_${id}`,
    }, this.emitSelectionChange);
  };

  onSearchDrivers = (e) => {
    this.setState({ searchKey: e.target.value });
  };

  getPersonalVehicles = (id) => {
    const { vehicleStore } = this.props.store;

    if (!id) return false;

    const requestId = Date.now() + Math.random();
    this._personalVehiclesRequestId = requestId;

    // clear vehicle states
    this.setState({
      selectedVehicleId: null,
      selectedVehicleIdAlias: null,
      personalVehicleLoading: true,
    }, this.emitSelectionChange);

    vehicleStore.getPersonalVehicles(id, { size: 10, page_number: 0 }).then((res) => {
      if (this._personalVehiclesRequestId !== requestId) return;

      if (res.ok && res.status === 200 && res.data && res.data.results) {
        this.setState({
          personalVehicles: res.data.results,
          personalVehicleLoading: false,
        });
      } else {
        this.setState({
          personalVehicles: [],
          personalVehicleLoading: false,
        });
      }
    }).catch(() => {
      if (this._personalVehiclesRequestId !== requestId) return;

      this.setState({
        personalVehicles: [],
        personalVehicleLoading: false,
      });
    });
  };

  getCompanyVehicles = () => {
    const { vehicleStore } = this.props.store;
    const courierId = _.get(this, 'props.store.userStore.account.id');

    if (!courierId) {
      this.setState({ companyVehicles: [] });
      return false;
    }

    vehicleStore.getCompanyVehicles(courierId, { size: 10, page_number: 0 }).then((res) => {
      if (res.ok && res.status === 200 && res.data && res.data.results) {
        this.setState({ companyVehicles: res.data.results });
      } else {
        this.setState({ companyVehicles: [] });
      }
    }).catch(() => {
      this.setState({ companyVehicles: [] });
    });
  };

  render() {
    const { selectedDriverId, selectedVehicleId, searchKey, personalVehicles, companyVehicles, selectedVehicleIdAlias, personalVehicleLoading } =
      this.state;
    const { store, assignment, driverIdSelected } = this.props;
    const { assignmentStore, driverStore } = store;
    const { filteredShowingStops } = assignmentStore;
    const { loadingDrivers, drivers } = driverStore;
    const onSelectDriver = this.onSelectDriver;
    const cities = filteredShowingStops && filteredShowingStops.map((s) => _.capitalize(s.shipment.dropoff_address.city));
    const citiesUniq = cities ? _.uniq(cities) : [];
    const vehicleTypeAndCapacity = [
      { type: 'SEDAN', capacity: 22 },
      { type: 'SUV', capacity: 30 },
      { type: 'MINIVAN', capacity: 60 },
      { type: 'VAN', capacity: 100 },
      { type: 'CARGOVAN', capacity: 120 },
      { type: 'TRUCK', capacity: 500 },
    ];
    const workload = assignment.workload || assignment.shipment_count || 0;
    const suitableVehicles = vehicleTypeAndCapacity.filter((vh) => vh.capacity >= workload);
    const recommendVehicle = suitableVehicles.length > 0
      ? _.minBy(suitableVehicles, 'capacity')
      : _.maxBy(vehicleTypeAndCapacity, 'capacity');

    if (loadingDrivers) {
      return (
        <Box py={3}>
          <JLoading />
        </Box>
      );
    }

    if (!drivers || drivers.length < 1) {
      return <Box>You do not have any drivers. Please create some new one!</Box>;
    }

    const filteredDrivers = _.cloneDeep(drivers || [])
      .map((dr) => {
        dr.first_name = _.capitalize(dr.first_name);
        dr.last_name = _.capitalize(dr.last_name);
        return dr;
      })
      .filter(
        (dr) =>
          (dr.first_name + ' ' + dr.last_name).toLowerCase().indexOf(searchKey.trim().toLowerCase()) > -1 ||
          (dr.id + '').indexOf(searchKey.trim().toLowerCase()) > -1,
      );
    const sortedDrivers = _.sortBy(filteredDrivers, ['first_name', 'last_name']) || [];

    return (
      <Box sx={styles.container}>
        <Grid container>
          <Grid size={12}>
            <Box py={1}>
              <strong>
                Assignment {assignment.label} - {assignment.shipment_count} Shipments
                {assignment?.warehouse_id ? ` - Warehouse #${assignment.warehouse_id}` : ''}
                {assignment?.region_code ? ` - ${assignment.region_code}` : ''}
              </strong>
            </Box>
            <Box py={1} sx={styles.grayText}>
              <small>
                {suitableVehicles.length > 0 ? (
                  <>Recommend min vehicle size: <strong>{recommendVehicle?.type}</strong></>
                ) : (
                  <>No suitable vehicle found. Largest available: <strong>{recommendVehicle?.type}</strong></>
                )}
              </small>
            </Box>
          </Grid>
          <Grid flex={1} />
          {/*<Grid size={6}>*/}
          {/*  <Box py={1}><strong>{citiesUniq.join('/ ')}</strong></Box>*/}
          {/*</Grid>*/}
        </Grid>
        <Box>
          <Grid container spacing={3}>
            <Grid size={6} sx={styles.listContainer}>
              <Box sx={styles.listWrapper}>
                <Box py={1}>
                  <TextField
                    onChange={this.onSearchDrivers}
                    value={searchKey}
                    variant="outlined"
                    label="Search drivers by name, ID..."
                    size="small"
                    fullWidth
                    style={{ backgroundColor: '#fff' }}
                  />
                </Box>
                <Box sx={styles.list}>
                  <Grid container direction="column" spacing={1}>
                    <List
                      style={{ height: this.HEIGHT, width: '100%' }}
                      rowCount={sortedDrivers.length}
                      rowHeight={this.ITEMSIZE}
                      rowComponent={Row}
                      rowProps={{
                        selectedDriverId,
                        onSelectDriver,
                        drivers: sortedDrivers,
                      }}
                    />
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid size={6} sx={styles.listContainer}>
              <PersonalVehicles
                driverIdSelected={driverIdSelected}
                loading={personalVehicleLoading}
                vehicles={personalVehicles}
                onSelectVehicle={this.onSelectPersonalVehicle}
                selectedVehicleId={selectedVehicleId}
              />
              <CompanyVehicles
                driverIdSelected={driverIdSelected}
                vehicles={companyVehicles}
                onSelectVehicle={this.onSelectCompanyVehicle}
                selectedVehicleId={selectedVehicleId}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }
}

const AssignDriverCompose = compose(inject('store'), observer)(AssignDriver);

export default AssignDriverCompose;

export function PersonalVehicles({ loading = false, vehicles = [], selectedVehicleId = null, onSelectVehicle = () => {/* empty */}, ...props }) {
  return (
    <Box sx={styles.listWrapperFlexible}>
      <Grid container direction="column" spacing={1}>
        {!loading ? (
          vehicles.length ? (
            vehicles.map((vehicle, i) => (
              <Grid key={`vehicle-${i}`}>
                <Box
                  sx={{
                    ...styles.cardContainer,
                    ...(vehicle.id === selectedVehicleId && styles.cardSelected),
                  }}
                >
                  <Grid container spacing={1} onClick={() => onSelectVehicle(vehicle.id)}>
                    <Grid size={11}>
                      <Box p={0.5} sx={styles.cardLabel}>
                        {_.get(vehicle, 'vehicle_name', _.get(vehicle, 'category', _.get(vehicle, 'license_plate')))}
                      </Box>
                      <Box p={0.5} sx={{ ...styles.cardSubtitle, ...styles.grayText }}>
                        {[
                          _.get(vehicle, 'car.year', '-'),
                          _.get(vehicle, 'car.make', '-'),
                          _.get(vehicle, 'car.model', '-'),
                          _.get(vehicle, 'car.submodel', '-'),
                        ].join(' ')}
                      </Box>
                    </Grid>
                    <Grid size={1} style={{ alignSelf: 'center' }}>
                      <Radio checked={vehicle.id === selectedVehicleId} name="driver-id" color="primary" size='small' sx={{ p: 0 }} />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))
          ) : (
            <Grid>No vehicles</Grid>
          )
        ) : (
          <Grid>Loading...</Grid>
        )}
      </Grid>
    </Box>
  );
}

const DEFAULT_KEY_SEARCH = ['vehicle_name', 'id', 'color', 'car.year', 'car.make', 'car.model', 'car.submodel', 'car.category', 'car.type'];

export function CompanyVehicles({
  keySearch = DEFAULT_KEY_SEARCH,
  vehicles = [],
  selectedVehicleId = null,
  onSelectVehicle = () => {/* empty */},
  ...props
}) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(vehicles);
  }, [vehicles]);

  // Create a stable debounced search function
  const debouncedSearch = useMemo(
    () =>
      _.debounce((searchValue, vehicleList) => {
        if (searchValue) {
          const newItems = vehicleList.filter((item) => {
            const keySearchMap = keySearch
              .map((key) => _.get(item, key, ''))
              .filter((key) => key)
              .join(' ')
              .toLowerCase();
            return _.includes(keySearchMap, searchValue.toLowerCase());
          });
          setItems(newItems);
        } else {
          setItems(vehicleList);
        }
      }, 300),
    [keySearch]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const onSearch = useCallback(
    ({ target: { value } }) => {
      debouncedSearch(value, vehicles);
    },
    [debouncedSearch, vehicles]
  );

  return (
    <Box sx={styles.listWrapper}>
      <Box mb={1} bgcolor={'primary.white'}>
        <TextField
          onChange={onSearch}
          placeholder="Search company vehicles..."
          variant="outlined"
          size="small"
          fullWidth
          style={{ backgroundColor: '#fff' }}
        />
      </Box>
      <Box sx={styles.listWrapperScroll}>
        <Grid container direction="column" spacing={1} style={{ marginBottom: 16 }}>
          {items && items.length > 0 ? (
            items.map((vehicle, i) => (
              <Grid key={`vehicle-${i}`}>
                <Box
                  sx={{
                    ...styles.cardContainer,
                    ...(vehicle.id === selectedVehicleId && styles.cardSelected),
                  }}
                >
                  <Grid container spacing={1} onClick={() => onSelectVehicle(vehicle.id)}>
                    <Grid size={11}>
                      <Grid container>
                        <Grid size={2} style={{ alignSelf: 'center' }}>
                          <Box>
                            <img src={`/assets/images/logo.png`} style={styles.cardAvatar} alt="" />
                          </Box>
                        </Grid>
                        <Grid size={10}>
                          <Box p={0.5} sx={styles.cardLabel}>
                            {_.get(vehicle, 'vehicle_name', _.get(vehicle, 'category', _.get(vehicle, 'license_plate')))}
                          </Box>
                          <Box p={0.5} sx={{ ...styles.cardSubtitle, ...styles.grayText }}>
                            {[
                              _.get(vehicle, 'car.year', '-'),
                              _.get(vehicle, 'car.make', '-'),
                              _.get(vehicle, 'car.model', '-'),
                              _.get(vehicle, 'car.submodel', '-'),
                            ].join(' ')}
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={1} style={{ alignSelf: 'center' }}>
                      <Radio checked={vehicle.id === selectedVehicleId} name="driver-id" color="primary" size='small' sx={{ p: 0 }} />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            ))
          ) : (
            <Grid>
              <Box sx={{ ...styles.cardContainer, textAlign: 'center', color: 'text.secondary' }}>
                No company vehicles found
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}
